import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axios";

export default function InvoiceView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchInvoice();
    }, [id]);

    const fetchInvoice = async () => {
        try {
            const res = await api.get(`/api/invoices/${id}`);
            if (res.data && res.data.data) {
                setInvoice(res.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch invoice", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = () => {
        // We can just open the PDF endpoint in a new tab because it will trigger a download
        const token = localStorage.getItem("token");
        const downloadUrl = `${api.defaults.baseURL}/api/invoices/${id}/pdf?token=${token}`; 
        // Note: For sanctum API token in URL to work, backend might need to read token from URL,
        // Alternatively, use axios to fetch blob and create object URL.
        
        api.get(`/api/invoices/${id}/pdf`, { responseType: 'blob' })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Invoice-${invoice.invoice_number}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch((error) => console.error("Error downloading PDF", error));
    };

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return <div className="text-center mt-5"><div className="spinner-border text-primary" /></div>;
    }

    if (!invoice) {
        return <div className="text-center mt-5 text-danger">Invoice not found!</div>;
    }

    return (
        <div className="container mt-4 mb-5">
            {/* Action Buttons - Hidden during print */}
            <div className="d-flex justify-content-between mb-4 d-print-none">
                <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                    <i className="bi bi-arrow-left"></i> Back
                </button>
                <div>
                    <button className="btn btn-primary me-2" onClick={handlePrint}>
                        <i className="bi bi-printer"></i> Print
                    </button>
                    <button className="btn btn-success" onClick={handleDownloadPdf}>
                        <i className="bi bi-file-earmark-pdf"></i> Download PDF
                    </button>
                </div>
            </div>

            {/* Printable Invoice Area */}
            <div className="card shadow-sm p-5 printable-area">
                <div className="row mb-5">
                    <div className="col-md-6">
                        <h2 className="text-primary mb-3">Iconic Greenland</h2>
                        <address>
                            <strong>Head Office</strong><br />
                            123 Real Estate Avenue<br />
                            Business District<br />
                            Phone: +1 234 567 8900
                        </address>
                    </div>
                    <div className="col-md-6 text-md-end">
                        <h1 className="text-muted">INVOICE</h1>
                        <p className="mb-1"><strong>Invoice #</strong> {invoice.invoice_number}</p>
                        <p className="mb-1"><strong>Date:</strong> {new Date(invoice.invoice_date).toLocaleDateString()}</p>
                        <p className="mb-1"><strong>Due Date:</strong> {new Date(invoice.due_date).toLocaleDateString()}</p>
                        <span className={`badge ${invoice.status === 'paid' ? 'bg-success' : 'bg-warning'}`}>
                            {invoice.status.toUpperCase()}
                        </span>
                    </div>
                </div>

                <div className="row mb-5">
                    <div className="col-md-6">
                        <h5 className="border-bottom pb-2">Billed To:</h5>
                        <p className="mb-1"><strong>{invoice.customer?.name}</strong></p>
                        <p className="mb-1">{invoice.customer?.mobile_number}</p>
                        <p className="mb-1">{invoice.customer?.email}</p>
                    </div>
                    <div className="col-md-6 text-md-end">
                        <h5 className="border-bottom pb-2">Sale Information:</h5>
                        <p className="mb-1"><strong>Property:</strong> {invoice.property?.name}</p>
                        <p className="mb-1"><strong>Unit:</strong> {invoice.unit?.unit_number || 'N/A'}</p>
                        <p className="mb-1"><strong>Agent/Sold By:</strong> {invoice.booking?.soldBy?.name || 'N/A'}</p>
                    </div>
                </div>

                <div className="table-responsive mb-4">
                    <table className="table table-bordered">
                        <thead className="table-light">
                            <tr>
                                <th>Description</th>
                                <th className="text-end">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    Property Booking Payment <br/>
                                    <small className="text-muted">{invoice.property?.name} - Unit {invoice.unit?.unit_number || 'N/A'}</small>
                                </td>
                                <td className="text-end">BDT {Number(invoice.total_amount).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="row">
                    <div className="col-md-6 offset-md-6">
                        <table className="table table-borderless">
                            <tbody>
                                <tr>
                                    <th className="text-end">Total Amount:</th>
                                    <td className="text-end border-bottom">BDT {Number(invoice.total_amount).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                </tr>
                                <tr>
                                    <th className="text-end">Paid Amount:</th>
                                    <td className="text-end border-bottom">BDT {Number(invoice.paid_amount).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                </tr>
                                <tr>
                                    <th className="text-end">Amount Due:</th>
                                    <td className="text-end h5 text-danger"><strong>BDT {Number(invoice.due_amount).toLocaleString(undefined, {minimumFractionDigits: 2})}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-5 text-center text-muted border-top pt-3">
                    <p className="mb-0">Thank you for choosing Iconic Greenland. For inquiries, please contact support.</p>
                </div>
            </div>

            {/* Print Styles */}
            <style>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .printable-area, .printable-area * {
                        visibility: visible;
                    }
                    .printable-area {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
}
